import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ProgramDetailResponse } from "../interfaces/Program";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";

interface ProgramState {
  loading: boolean;
  user: any | null;
  error: string | null;
  isLoggedIn: boolean;
  data: Programs[] | null;
  data1: ProgramDetailResponse | null;
}

interface FetchProgramsParams {
  search?: string;
  sortField?: string;
  sort?: string;
  limit?: number;
  page?: number;
}

const initialState: ProgramState = {
  loading: false,
  user: null,
  error: null,
  isLoggedIn: false,
  data: null,
  data1: null,
};
interface PublishProgram {
  programId: string;
  publish: boolean;
}

interface Programs {
  id: string;
  bookedSlots:number;
  title: string;
  cost: string;
  startDate: string;
  startTime: number;
  endTime: number;
  totalCounsellings: number;
  maxParticipants: number;
  zoomLink: string;
  description: string;
  icon: string;
  slug: string;
  publish: boolean;
  healthCondition: string;
}

interface ParsedData {  
    accessToken?: string;
}

export const fetchProgramsAll = createAsyncThunk(
  "user/fetchProgramsAll",
  async (params: any, thunkAPI) => {
    try {
      const response = await apiClient.get('/api/v1/patients/programs', { params });
      return response.data.data.programs;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const fetchProgramsFilter = createAsyncThunk(
  "user/fetchProgramsFilter",
  async ({ programSlug, limit }: { programSlug: string; limit?: number }, thunkAPI) => {
    try {
      const response = await apiClient.get('/api/v1/patients/programs/latest', {
        params: { slug: programSlug, limit },
      });
      return response.data.data.programs;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const fetchDataProgramDetails = createAsyncThunk(
  "user/fetchDataProgramDetails",
  async (programSlug: string, thunkAPI) => {
    try {
      localStorage.setItem('SetSlug', programSlug);
      const response = await apiClient.get(`/api/v1/patients/programs/${programSlug}`);
      return response.data.data.programs;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const fetchPrograms = createAsyncThunk(
  "user/fetchPrograms",
  async (params: any, thunkAPI) => {
    try {
      const response = await apiClient.get('/api/v1/programs', { params });
      return response.data.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const deleteProgram = createAsyncThunk(
  "user/deleteProgram",
  async (programId: string, { getState, rejectWithValue }) => {
    try {
      await apiClient.delete(`/api/v1/programs/${programId}`);
      return programId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const publishProgram = createAsyncThunk(
  "user/publishProgram",
  async (programData: PublishProgram, { getState, rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `/api/v1/programs/publish/${programData.programId}`,
        { publish: programData.publish }
      );
      toast.success(response.data.message);
      return response.data;
    } catch (error: any) {      
      toast.error("The publication was unsuccessful. Kindly complete all information needed");  
      return rejectWithValue(error.message);
    }
  }
);

const programSlice = createSlice({
  name: "programs",
  initialState,
  reducers: {
    clearProgramDetail: (state) => {
      state.data1 = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataProgramDetails.pending, (state) => {
        state.loading = true;
        state.data1 = null;
      })
      .addCase(fetchDataProgramDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data1 = action.payload;
      })
      .addCase(fetchDataProgramDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred";
      })    
      .addCase(fetchProgramsFilter.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProgramsFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchProgramsFilter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred";
      }).addCase(fetchProgramsAll.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProgramsAll.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchProgramsAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred";
      })
      .addCase(fetchPrograms.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPrograms.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchPrograms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred";
      })
      .addCase(deleteProgram.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProgram.fulfilled, (state, action) => {
        state.loading = false;

        // Ensure that state.data is an array before filtering
        if (Array.isArray(state.data)) {
          // Remove the deleted health condition from the data array
          const deletedId = action.payload;
          state.data = state.data.filter(
            (Programs) => Programs.id !== deletedId
          );
        }
      })
      .addCase(deleteProgram.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearProgramDetail } = programSlice.actions;
export default programSlice.reducer;
